import React, { useState, useEffect } from 'react';
import { aiAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { TrendingUp, AlertTriangle, RefreshCw, Loader2, Lightbulb, BarChart3, DollarSign, Package } from 'lucide-react';
import { toast } from 'sonner';

const URGENCY_COLORS = {
  critical: 'bg-red-100 text-red-700',
  high: 'bg-amber-100 text-amber-700',
  medium: 'bg-blue-100 text-blue-700',
  low: 'bg-slate-100 text-slate-600'
};

const IMPACT_COLORS = { high: 'destructive', medium: 'outline', low: 'secondary' };

export default function Forecasting() {
  const [forecast, setForecast] = useState(null);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);

  useEffect(() => { loadCachedForecast(); }, []);

  const loadCachedForecast = async () => {
    setLoading(true);
    try {
      const res = await aiAPI.getForecastDashboard();
      setForecast(res.data);
    } catch {
      // No cached forecast
    } finally {
      setLoading(false);
    }
  };

  const generateForecast = async () => {
    setGenerating(true);
    try {
      const res = await aiAPI.getForecast();
      setForecast(res.data);
      toast.success('Forecast generated successfully');
    } catch {
      toast.error('Failed to generate forecast');
    } finally {
      setGenerating(false);
    }
  };

  const alerts = forecast?.reorder_alerts || [];
  const trends = forecast?.trends || [];
  const recs = forecast?.recommendations || [];
  const f30 = forecast?.forecast_30_days || {};

  return (
    <div className="space-y-6" data-testid="forecasting-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Inventory Forecasting</h1>
          <p className="text-slate-500 mt-1">AI-powered predictions and reorder recommendations</p>
        </div>
        <Button className="btn-navy" onClick={generateForecast} disabled={generating} data-testid="generate-forecast-btn">
          {generating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
          {generating ? 'Analyzing...' : 'Generate Forecast'}
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-24">
          <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
        </div>
      ) : !forecast ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-20">
            <TrendingUp className="w-16 h-16 text-slate-300 mb-4" />
            <h3 className="text-lg font-semibold mb-1">No Forecast Available</h3>
            <p className="text-slate-500 text-sm mb-4">Click "Generate Forecast" to analyze your inventory data</p>
            <Button className="btn-navy" onClick={generateForecast} disabled={generating}>
              {generating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <TrendingUp className="w-4 h-4 mr-2" />}
              Generate Now
            </Button>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Summary + 30-day cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card className="md:col-span-2">
              <CardContent className="p-5">
                <h3 className="text-sm font-semibold text-slate-500 mb-2">AI Summary</h3>
                <p className="text-sm text-slate-700 leading-relaxed">{forecast.summary}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-5">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                    <AlertTriangle className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{f30.items_at_risk || 0}</p>
                    <p className="text-sm text-slate-500">Items at Risk</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-5">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                    <DollarSign className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">${(f30.estimated_reorder_cost || 0).toLocaleString()}</p>
                    <p className="text-sm text-slate-500">Est. Reorder Cost</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Reorder Alerts */}
          {alerts.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-500" /> Reorder Alerts
                </CardTitle>
                <CardDescription>Items predicted to need reordering</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Item</TableHead>
                      <TableHead className="text-right">Current</TableHead>
                      <TableHead className="text-right">Min Stock</TableHead>
                      <TableHead className="text-right">Days Until Stockout</TableHead>
                      <TableHead className="text-right">Recommended Qty</TableHead>
                      <TableHead className="text-right">Est. Cost</TableHead>
                      <TableHead>Urgency</TableHead>
                      <TableHead>Reason</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {alerts.map((a, i) => (
                      <TableRow key={i}>
                        <TableCell className="font-medium">{a.item_name}</TableCell>
                        <TableCell className="text-right">{a.current_stock}</TableCell>
                        <TableCell className="text-right">{a.min_stock}</TableCell>
                        <TableCell className="text-right font-medium">{a.estimated_days_until_stockout}</TableCell>
                        <TableCell className="text-right font-medium">{a.recommended_order_qty}</TableCell>
                        <TableCell className="text-right">${(a.estimated_cost || 0).toLocaleString()}</TableCell>
                        <TableCell>
                          <Badge className={URGENCY_COLORS[a.urgency] || URGENCY_COLORS.medium}>
                            {a.urgency}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm text-slate-600 max-w-[200px]">{a.reason}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}

          {/* Trends + Recommendations */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {trends.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-blue-500" /> Trends & Insights
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {trends.map((t, i) => (
                    <div key={i} className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                      <Badge variant={IMPACT_COLORS[t.impact] || 'outline'} className="mt-0.5 shrink-0">
                        {t.impact}
                      </Badge>
                      <div>
                        <p className="text-sm text-slate-700">{t.insight}</p>
                        <p className="text-xs text-slate-400 mt-1 capitalize">{t.category}</p>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            {recs.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Lightbulb className="w-5 h-5 text-amber-500" /> Recommendations
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3">
                    {recs.map((r, i) => (
                      <li key={i} className="flex items-start gap-3 p-3 bg-amber-50 rounded-lg">
                        <span className="w-6 h-6 rounded-full bg-amber-200 text-amber-700 flex items-center justify-center text-xs font-bold shrink-0">{i + 1}</span>
                        <p className="text-sm text-slate-700">{r}</p>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}
          </div>
        </>
      )}
    </div>
  );
}
